import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import DashboardLayout from '../components/layout/DashboardLayout';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

// Dropdown options
const experienceLevelOptions = [
  { value: 'internship', label: 'Internship' },
  { value: 'entry', label: 'Entry Level' },
  { value: 'mid', label: 'Mid Level' },
  { value: 'senior', label: 'Senior Level' },
  { value: 'executive', label: 'Executive' }
];

const jobTypeOptions = [
  { value: 'full-time', label: 'Full-time' },
  { value: 'part-time', label: 'Part-time' },
  { value: 'contract', label: 'Contract' },
  { value: 'internship', label: 'Internship' }
];

const sponsorshipOptions = [
  { value: '', label: 'None' },
  { value: 'visa_sponsorship', label: 'Visa/H1B Sponsorship' },
  { value: 'security_clearance', label: 'Security Clearance Required' }
];

// Map spec values to DB values
const mapExperienceLevel = (level) => {
  if (!level) return '';
  const map = {
    'Internship': 'internship',
    'Entry Level': 'entry',
    'Mid Level': 'mid',
    'Senior Level': 'senior',
    'Executive': 'executive'
  };
  return map[level] || level.toLowerCase().replace(' level', '').replace(' ', '-');
};

const mapJobType = (type) => {
  if (!type) return '';
  const map = {
    'Full-time': 'full-time',
    'Part-time': 'part-time',
    'Contract': 'contract',
    'Internship': 'internship'
  };
  return map[type] || type.toLowerCase();
};

const getEmptyFormData = () => ({
  company_name: '',
  job_title: '',
  location: '',
  job_url: '',
  experience_level: '',
  job_type: '',
  compensation: '',
  skills: [],
  job_description_structured: {
    companyOverview: '',
    roleSummary: '',
    responsibilities: [],
    requiredQualifications: [],
    preferredQualifications: [],
    benefits: '',
    additionalRequirements: ''
  },
  sponsorship_and_legal: ''
});

const AdminJobs = () => {
  const { user, session, getToken, loading: authLoading } = useAuth();
  const navigate = useNavigate();

  // Admin check state
  const [isAdmin, setIsAdmin] = useState(null);
  const [checkingAdmin, setCheckingAdmin] = useState(true);

  // Jobs list state
  const [jobs, setJobs] = useState([]);
  const [totalJobs, setTotalJobs] = useState(0);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');

  // Form state
  const [showForm, setShowForm] = useState(false);
  const [editingJob, setEditingJob] = useState(null);
  const [formData, setFormData] = useState(getEmptyFormData());
  const [skillInput, setSkillInput] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // JSON Modal state
  const [showJsonModal, setShowJsonModal] = useState(false);
  const [jsonInput, setJsonInput] = useState('');
  const [jsonError, setJsonError] = useState('');

  // Check if user is admin
  useEffect(() => {
    const checkAdminStatus = async () => {
      const token = getToken();
      if (!token) {
        setIsAdmin(false);
        setCheckingAdmin(false);
        return;
      }

      try {
        const response = await fetch(`${API_URL}/api/admin/check`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (response.ok) {
          const data = await response.json();
          setIsAdmin(data.is_admin);
        } else {
          setIsAdmin(false);
        }
      } catch (err) {
        console.error('Failed to check admin status:', err);
        setIsAdmin(false);
      } finally {
        setCheckingAdmin(false);
      }
    };

    if (!authLoading && session && user) {
      checkAdminStatus();
    }
  }, [authLoading, session, user, getToken]);

  // Load jobs when admin is confirmed
  useEffect(() => {
    if (isAdmin) {
      loadJobs();
    }
  }, [isAdmin]);

  const loadJobs = async () => {
    const token = getToken();
    if (!token) return;

    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (searchQuery) params.append('search', searchQuery);

      const response = await fetch(`${API_URL}/api/admin/jobs${params.toString() ? '?' + params.toString() : ''}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch jobs');
      }

      const data = await response.json();
      setJobs(data.jobs || []);
      setTotalJobs(data.total || 0);
    } catch (err) {
      console.error('Failed to load jobs:', err);
      setJobs([]);
      setTotalJobs(0);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData(getEmptyFormData());
    setSkillInput('');
    setEditingJob(null);
    setError('');
  };

  const handleOpenForm = (job = null) => {
    if (job) {
      setEditingJob(job);
      const desc = job.job_description_structured || {};
      setFormData({
        company_name: job.company_name || '',
        job_title: job.job_title || '',
        location: job.location || '',
        job_url: job.job_url || '',
        experience_level: job.experience_level || '',
        job_type: job.job_type || '',
        compensation: job.compensation || '',
        skills: job.skills || [],
        job_description_structured: {
          companyOverview: desc.companyOverview || '',
          roleSummary: desc.roleSummary || '',
          responsibilities: desc.responsibilities || [],
          requiredQualifications: desc.requiredQualifications || [],
          preferredQualifications: desc.preferredQualifications || [],
          benefits: desc.benefits || '',
          additionalRequirements: desc.additionalRequirements || ''
        },
        sponsorship_and_legal: job.sponsorship_and_legal || ''
      });
    } else {
      resetForm();
    }
    setShowForm(true);
    setSuccess('');
  };

  const handleCloseForm = () => {
    setShowForm(false);
    resetForm();
  };

  // Skills handling
  const addSkill = (skill) => {
    const trimmed = skill.trim();
    if (trimmed && !formData.skills.includes(trimmed)) {
      setFormData({
        ...formData,
        skills: [...formData.skills, trimmed]
      });
    }
    setSkillInput('');
  };

  const handleSkillInputKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      const skills = skillInput.split(',').map(s => s.trim()).filter(s => s);
      skills.forEach(s => addSkill(s));
    }
  };

  const removeSkill = (skillToRemove) => {
    setFormData({
      ...formData,
      skills: formData.skills.filter(s => s !== skillToRemove)
    });
  };

  // Update structured description field
  const updateStructuredField = (field, value) => {
    setFormData({
      ...formData,
      job_description_structured: {
        ...formData.job_description_structured,
        [field]: value
      }
    });
  };

  // Convert array field to newline-separated text for display
  const arrayToText = (arr) => (arr || []).join('\n');

  // Convert newline-separated text to array
  const textToArray = (text) => text.split('\n').map(s => s.trim()).filter(s => s);

  // JSON Import handling
  const handleOpenJsonModal = () => {
    setJsonInput('');
    setJsonError('');
    setShowJsonModal(true);
  };

  const handleJsonImport = () => {
    try {
      const parsed = JSON.parse(jsonInput);

      // Validate required fields
      const required = ['companyName', 'jobTitle', 'location', 'experienceLevel', 'jobType', 'skills'];
      const missing = required.filter(f => {
        const val = parsed[f];
        return !val || (Array.isArray(val) && val.length === 0);
      });

      if (missing.length > 0) {
        setJsonError(`Missing required fields: ${missing.join(', ')}`);
        return;
      }

      // Validate at least one description section
      const desc = parsed.jobDescription || {};
      const hasContent = desc.companyOverview || desc.roleSummary ||
        (desc.responsibilities?.length > 0) || (desc.requiredQualifications?.length > 0) ||
        (desc.preferredQualifications?.length > 0) || desc.benefits || desc.additionalRequirements;

      if (!hasContent) {
        setJsonError('At least one job description section must be filled');
        return;
      }

      // Map camelCase JSON to snake_case form
      setFormData({
        company_name: parsed.companyName || '',
        job_title: parsed.jobTitle || '',
        location: parsed.location || '',
        job_url: parsed.jobUrl || '',
        experience_level: mapExperienceLevel(parsed.experienceLevel),
        job_type: mapJobType(parsed.jobType),
        compensation: parsed.compensation || '',
        skills: parsed.skills || [],
        job_description_structured: {
          companyOverview: desc.companyOverview || '',
          roleSummary: desc.roleSummary || '',
          responsibilities: desc.responsibilities || [],
          requiredQualifications: desc.requiredQualifications || [],
          preferredQualifications: desc.preferredQualifications || [],
          benefits: desc.benefits || '',
          additionalRequirements: desc.additionalRequirements || ''
        },
        sponsorship_and_legal: parsed.sponsorshipAndLegal || ''
      });

      setShowJsonModal(false);
      setShowForm(true);
      setSuccess('Job listing imported successfully. Review and edit as needed.');
    } catch (e) {
      setJsonError('Invalid JSON format. Please check for syntax errors.');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    const token = getToken();
    if (!token) return;

    // Validate required fields
    if (!formData.company_name.trim()) {
      setError('Company name is required');
      return;
    }
    if (!formData.job_title.trim()) {
      setError('Job title is required');
      return;
    }
    if (!formData.location.trim()) {
      setError('Location is required');
      return;
    }
    if (!formData.job_url.trim()) {
      setError('Job URL is required');
      return;
    }
    if (!formData.job_url.startsWith('http://') && !formData.job_url.startsWith('https://')) {
      setError('Please enter a valid URL starting with http:// or https://');
      return;
    }
    if (!formData.experience_level) {
      setError('Experience level is required');
      return;
    }
    if (!formData.job_type) {
      setError('Job type is required');
      return;
    }
    if (!formData.skills || formData.skills.length === 0) {
      setError('Skills must not be empty');
      return;
    }

    // Validate at least one description section
    const desc = formData.job_description_structured;
    const hasContent = desc.companyOverview || desc.roleSummary ||
      (desc.responsibilities?.length > 0) || (desc.requiredQualifications?.length > 0) ||
      (desc.preferredQualifications?.length > 0) || desc.benefits || desc.additionalRequirements;

    if (!hasContent) {
      setError('At least one job description section must be filled');
      return;
    }

    try {
      setSubmitting(true);

      const url = editingJob
        ? `${API_URL}/api/admin/jobs/${editingJob.id}`
        : `${API_URL}/api/admin/jobs`;

      const response = await fetch(url, {
        method: editingJob ? 'PUT' : 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to save job');
      }

      setSuccess(editingJob ? 'Job updated successfully!' : 'Job created successfully!');
      handleCloseForm();
      loadJobs();
    } catch (err) {
      console.error('Failed to save job:', err);
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (jobId) => {
    if (!window.confirm('Are you sure you want to delete this job? This action cannot be undone.')) {
      return;
    }

    const token = getToken();
    if (!token) return;

    try {
      const response = await fetch(`${API_URL}/api/admin/jobs/${jobId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to delete job');
      }

      setSuccess('Job deleted successfully!');
      loadJobs();
    } catch (err) {
      console.error('Failed to delete job:', err);
      setError(err.message);
    }
  };

  const handleSearch = () => {
    loadJobs();
  };

  // Loading states
  if (authLoading || checkingAdmin) {
    return (
      <div style={{ minHeight: '100vh', background: 'var(--bg-base)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-primary)' }}>
        <p style={{ color: 'var(--text-tertiary)' }}>Loading...</p>
      </div>
    );
  }

  // Not logged in
  if (!user || !session) {
    navigate('/login');
    return null;
  }

  // Not admin
  if (!isAdmin) {
    return (
      <DashboardLayout title="Access Denied">
        <div style={{
          padding: 'var(--space-4xl)',
          textAlign: 'center',
          background: 'var(--bg-surface)',
          border: '1px solid var(--border-subtle)',
          borderRadius: 'var(--radius-xl)'
        }}>
          <div style={{
            width: '64px',
            height: '64px',
            background: 'rgba(239, 68, 68, 0.15)',
            borderRadius: '16px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto var(--space-md)'
          }}>
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="var(--red-500)" strokeWidth="2">
              <circle cx="12" cy="12" r="10"/>
              <line x1="15" y1="9" x2="9" y2="15"/>
              <line x1="9" y1="9" x2="15" y2="15"/>
            </svg>
          </div>
          <h2 style={{ fontSize: '1.5rem', color: 'var(--text-primary)', marginBottom: 'var(--space-sm)' }}>
            Admin Access Required
          </h2>
          <p style={{ fontSize: '1rem', color: 'var(--text-tertiary)' }}>
            You don't have permission to access the job admin panel.
          </p>
        </div>
      </DashboardLayout>
    );
  }

  const inputStyle = {
    width: '100%',
    padding: 'var(--space-md)',
    background: 'var(--bg-surface)',
    border: '1px solid var(--border-light)',
    borderRadius: 'var(--radius-md)',
    color: 'var(--text-primary)',
    fontSize: '1rem',
    outline: 'none',
    fontFamily: 'var(--font-body)',
    boxSizing: 'border-box'
  };

  const labelStyle = {
    display: 'block',
    marginBottom: 'var(--space-xs)',
    fontSize: '0.875rem',
    fontWeight: '600',
    color: 'var(--text-secondary)'
  };

  const skillTagStyle = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    padding: '4px 10px',
    background: 'rgba(99, 102, 241, 0.15)',
    border: '1px solid rgba(99, 102, 241, 0.3)',
    borderRadius: 'var(--radius-full)',
    fontSize: '0.875rem',
    color: 'var(--indigo-400)'
  };

  const removeSkillBtnStyle = {
    background: 'none',
    border: 'none',
    padding: 0,
    cursor: 'pointer',
    color: 'var(--indigo-400)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '16px',
    height: '16px',
    borderRadius: '50%',
    transition: 'background var(--duration-fast)'
  };

  return (
    <DashboardLayout
      title="Job Admin Panel"
      subtitle={`Manage job listings - ${totalJobs} total jobs`}
    >
      {/* Success/Error Messages */}
      {success && (
        <div style={{
          padding: 'var(--space-md)',
          background: 'rgba(34, 197, 94, 0.15)',
          border: '1px solid rgba(34, 197, 94, 0.3)',
          borderRadius: 'var(--radius-md)',
          color: 'var(--green-400)',
          marginBottom: 'var(--space-lg)'
        }}>
          {success}
        </div>
      )}
      {error && (
        <div style={{
          padding: 'var(--space-md)',
          background: 'rgba(239, 68, 68, 0.15)',
          border: '1px solid rgba(239, 68, 68, 0.3)',
          borderRadius: 'var(--radius-md)',
          color: 'var(--red-500)',
          marginBottom: 'var(--space-lg)'
        }}>
          {error}
        </div>
      )}

      {/* Action Bar */}
      <div style={{ display: 'flex', gap: 'var(--space-md)', marginBottom: 'var(--space-xl)', flexWrap: 'wrap' }}>
        <button
          onClick={() => handleOpenForm()}
          style={{
            padding: 'var(--space-md) var(--space-xl)',
            background: 'var(--gradient-primary)',
            color: 'white',
            border: 'none',
            borderRadius: 'var(--radius-lg)',
            fontSize: '1rem',
            fontWeight: '700',
            cursor: 'pointer',
            fontFamily: 'var(--font-body)',
            boxShadow: 'var(--glow-indigo)'
          }}
        >
          + Add New Job
        </button>

        <button
          onClick={handleOpenJsonModal}
          style={{
            padding: 'var(--space-md) var(--space-xl)',
            background: 'var(--bg-surface)',
            color: 'var(--text-primary)',
            border: '1px solid var(--border-light)',
            borderRadius: 'var(--radius-lg)',
            fontSize: '1rem',
            fontWeight: '600',
            cursor: 'pointer',
            fontFamily: 'var(--font-body)'
          }}
        >
          Paste JSON
        </button>

        <div style={{ flex: 1, display: 'flex', gap: 'var(--space-sm)' }}>
          <input
            type="text"
            placeholder="Search jobs..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
            style={{
              ...inputStyle,
              flex: 1
            }}
          />
          <button
            onClick={handleSearch}
            style={{
              padding: 'var(--space-md) var(--space-lg)',
              background: 'var(--bg-surface)',
              border: '1px solid var(--border-light)',
              borderRadius: 'var(--radius-md)',
              color: 'var(--text-primary)',
              fontSize: '1rem',
              cursor: 'pointer',
              fontFamily: 'var(--font-body)'
            }}
          >
            Search
          </button>
        </div>
      </div>

      {/* Jobs Table */}
      {loading ? (
        <p style={{ color: 'var(--text-tertiary)' }}>Loading jobs...</p>
      ) : jobs.length === 0 ? (
        <div style={{
          padding: 'var(--space-4xl)',
          textAlign: 'center',
          background: 'var(--bg-surface)',
          border: '1px solid var(--border-subtle)',
          borderRadius: 'var(--radius-xl)'
        }}>
          <p style={{ fontSize: '1.125rem', color: 'var(--text-tertiary)' }}>
            {searchQuery ? `No jobs found matching "${searchQuery}"` : 'No jobs yet. Click "Add New Job" to create one.'}
          </p>
        </div>
      ) : (
        <div style={{
          background: 'var(--bg-surface)',
          border: '1px solid var(--border-subtle)',
          borderRadius: 'var(--radius-xl)',
          overflow: 'hidden'
        }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: 'var(--bg-elevated)', borderBottom: '1px solid var(--border-subtle)' }}>
                <th style={{ padding: 'var(--space-md)', textAlign: 'left', color: 'var(--text-secondary)', fontWeight: '600', fontSize: '0.875rem' }}>Company</th>
                <th style={{ padding: 'var(--space-md)', textAlign: 'left', color: 'var(--text-secondary)', fontWeight: '600', fontSize: '0.875rem' }}>Job Title</th>
                <th style={{ padding: 'var(--space-md)', textAlign: 'left', color: 'var(--text-secondary)', fontWeight: '600', fontSize: '0.875rem' }}>Location</th>
                <th style={{ padding: 'var(--space-md)', textAlign: 'left', color: 'var(--text-secondary)', fontWeight: '600', fontSize: '0.875rem' }}>Type</th>
                <th style={{ padding: 'var(--space-md)', textAlign: 'left', color: 'var(--text-secondary)', fontWeight: '600', fontSize: '0.875rem' }}>Skills</th>
                <th style={{ padding: 'var(--space-md)', textAlign: 'left', color: 'var(--text-secondary)', fontWeight: '600', fontSize: '0.875rem' }}>Created</th>
                <th style={{ padding: 'var(--space-md)', textAlign: 'right', color: 'var(--text-secondary)', fontWeight: '600', fontSize: '0.875rem' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {jobs.map((job, index) => (
                <tr
                  key={job.id}
                  style={{
                    borderBottom: index < jobs.length - 1 ? '1px solid var(--border-subtle)' : 'none',
                    transition: 'background var(--duration-fast)'
                  }}
                  onMouseOver={(e) => e.currentTarget.style.background = 'var(--bg-elevated)'}
                  onMouseOut={(e) => e.currentTarget.style.background = 'transparent'}
                >
                  <td style={{ padding: 'var(--space-md)', color: 'var(--text-primary)', fontWeight: '600' }}>{job.company_name}</td>
                  <td style={{ padding: 'var(--space-md)', color: 'var(--text-primary)' }}>{job.job_title}</td>
                  <td style={{ padding: 'var(--space-md)', color: 'var(--text-tertiary)' }}>{job.location}</td>
                  <td style={{ padding: 'var(--space-md)' }}>
                    <div style={{ display: 'flex', gap: 'var(--space-xs)', flexWrap: 'wrap' }}>
                      {job.experience_level && (
                        <span style={{
                          padding: '2px 8px',
                          background: 'rgba(99, 102, 241, 0.15)',
                          border: '1px solid rgba(99, 102, 241, 0.3)',
                          borderRadius: 'var(--radius-full)',
                          fontSize: '0.75rem',
                          color: 'var(--indigo-400)',
                          textTransform: 'capitalize'
                        }}>
                          {job.experience_level}
                        </span>
                      )}
                      {job.job_type && (
                        <span style={{
                          padding: '2px 8px',
                          background: 'rgba(245, 158, 11, 0.15)',
                          border: '1px solid rgba(245, 158, 11, 0.3)',
                          borderRadius: 'var(--radius-full)',
                          fontSize: '0.75rem',
                          color: 'var(--amber-400)',
                          textTransform: 'capitalize'
                        }}>
                          {job.job_type}
                        </span>
                      )}
                    </div>
                  </td>
                  <td style={{ padding: 'var(--space-md)', color: 'var(--text-tertiary)', fontSize: '0.875rem' }}>
                    {job.skills?.length || 0} skills
                  </td>
                  <td style={{ padding: 'var(--space-md)', color: 'var(--text-muted)', fontSize: '0.875rem' }}>
                    {new Date(job.created_at).toLocaleDateString()}
                  </td>
                  <td style={{ padding: 'var(--space-md)', textAlign: 'right' }}>
                    <div style={{ display: 'flex', gap: 'var(--space-sm)', justifyContent: 'flex-end' }}>
                      <button
                        onClick={() => handleOpenForm(job)}
                        style={{
                          padding: 'var(--space-xs) var(--space-sm)',
                          background: 'rgba(99, 102, 241, 0.15)',
                          border: '1px solid rgba(99, 102, 241, 0.3)',
                          borderRadius: 'var(--radius-md)',
                          color: 'var(--indigo-400)',
                          fontSize: '0.75rem',
                          cursor: 'pointer',
                          fontFamily: 'var(--font-body)'
                        }}
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => handleDelete(job.id)}
                        style={{
                          padding: 'var(--space-xs) var(--space-sm)',
                          background: 'rgba(239, 68, 68, 0.15)',
                          border: '1px solid rgba(239, 68, 68, 0.3)',
                          borderRadius: 'var(--radius-md)',
                          color: 'var(--red-500)',
                          fontSize: '0.75rem',
                          cursor: 'pointer',
                          fontFamily: 'var(--font-body)'
                        }}
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* JSON Paste Modal */}
      {showJsonModal && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 'var(--sidebar-width)',
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.7)',
            backdropFilter: 'blur(8px)',
            WebkitBackdropFilter: 'blur(8px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: 'var(--space-xl)'
          }}
          onClick={() => setShowJsonModal(false)}
        >
          <div
            style={{
              maxWidth: '700px',
              maxHeight: '90vh',
              width: '100%',
              background: 'var(--bg-elevated)',
              border: '1px solid var(--border-light)',
              borderRadius: 'var(--radius-2xl)',
              padding: 'var(--space-2xl)',
              overflowY: 'auto',
              boxShadow: 'var(--shadow-lg)'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h2 style={{
              fontFamily: 'var(--font-display)',
              fontSize: '1.5rem',
              fontWeight: '700',
              marginBottom: 'var(--space-md)',
              color: 'var(--text-primary)'
            }}>
              Import Job from JSON
            </h2>
            <p style={{ color: 'var(--text-tertiary)', marginBottom: 'var(--space-lg)', fontSize: '0.875rem' }}>
              Paste the JSON output from Claude to auto-populate the form fields.
            </p>

            <textarea
              value={jsonInput}
              onChange={(e) => {
                setJsonInput(e.target.value);
                setJsonError('');
              }}
              style={{
                ...inputStyle,
                minHeight: '300px',
                fontFamily: 'monospace',
                fontSize: '0.875rem',
                resize: 'vertical',
                lineHeight: '1.5'
              }}
              placeholder='Paste JSON here...'
            />

            {jsonError && (
              <div style={{
                padding: 'var(--space-md)',
                background: 'rgba(239, 68, 68, 0.15)',
                border: '1px solid rgba(239, 68, 68, 0.3)',
                borderRadius: 'var(--radius-md)',
                color: 'var(--red-500)',
                marginTop: 'var(--space-md)',
                fontSize: '0.875rem'
              }}>
                {jsonError}
              </div>
            )}

            <div style={{ display: 'flex', gap: 'var(--space-md)', justifyContent: 'flex-end', marginTop: 'var(--space-xl)' }}>
              <button
                type="button"
                onClick={() => setShowJsonModal(false)}
                style={{
                  padding: 'var(--space-md) var(--space-xl)',
                  background: 'var(--bg-surface)',
                  color: 'var(--text-primary)',
                  border: '1px solid var(--border-light)',
                  borderRadius: 'var(--radius-lg)',
                  fontSize: '1rem',
                  fontWeight: '600',
                  fontFamily: 'var(--font-body)',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleJsonImport}
                style={{
                  padding: 'var(--space-md) var(--space-xl)',
                  background: 'var(--gradient-primary)',
                  color: 'white',
                  border: 'none',
                  borderRadius: 'var(--radius-lg)',
                  fontSize: '1rem',
                  fontWeight: '700',
                  fontFamily: 'var(--font-body)',
                  cursor: 'pointer',
                  boxShadow: 'var(--glow-indigo)'
                }}
              >
                Import
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Job Form Modal */}
      {showForm && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 'var(--sidebar-width)',
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.7)',
            backdropFilter: 'blur(8px)',
            WebkitBackdropFilter: 'blur(8px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: 'var(--space-xl)'
          }}
          onClick={handleCloseForm}
        >
          <div
            style={{
              maxWidth: '800px',
              maxHeight: '90vh',
              width: '100%',
              background: 'var(--bg-elevated)',
              border: '1px solid var(--border-light)',
              borderRadius: 'var(--radius-2xl)',
              padding: 'var(--space-2xl)',
              overflowY: 'auto',
              boxShadow: 'var(--shadow-lg)'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h2 style={{
              fontFamily: 'var(--font-display)',
              fontSize: '1.5rem',
              fontWeight: '700',
              marginBottom: 'var(--space-xl)',
              color: 'var(--text-primary)'
            }}>
              {editingJob ? 'Edit Job' : 'Add New Job'}
            </h2>

            <form onSubmit={handleSubmit}>
              {/* Row 1: Company Name, Job Title */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-lg)', marginBottom: 'var(--space-lg)' }}>
                <div>
                  <label style={labelStyle}>Company Name *</label>
                  <input
                    type="text"
                    value={formData.company_name}
                    onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
                    style={inputStyle}
                    placeholder="e.g., Google"
                  />
                </div>
                <div>
                  <label style={labelStyle}>Job Title *</label>
                  <input
                    type="text"
                    value={formData.job_title}
                    onChange={(e) => setFormData({ ...formData, job_title: e.target.value })}
                    style={inputStyle}
                    placeholder="e.g., Software Engineer"
                  />
                </div>
              </div>

              {/* Row 2: Location, Job URL */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-lg)', marginBottom: 'var(--space-lg)' }}>
                <div>
                  <label style={labelStyle}>Location *</label>
                  <input
                    type="text"
                    value={formData.location}
                    onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                    style={inputStyle}
                    placeholder="e.g., San Francisco, CA or Remote"
                  />
                </div>
                <div>
                  <label style={labelStyle}>Job URL *</label>
                  <input
                    type="text"
                    value={formData.job_url}
                    onChange={(e) => setFormData({ ...formData, job_url: e.target.value })}
                    style={inputStyle}
                    placeholder="https://..."
                  />
                </div>
              </div>

              {/* Row 3: Experience Level, Job Type */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-lg)', marginBottom: 'var(--space-lg)' }}>
                <div>
                  <label style={labelStyle}>Experience Level *</label>
                  <select
                    value={formData.experience_level}
                    onChange={(e) => setFormData({ ...formData, experience_level: e.target.value })}
                    style={inputStyle}
                  >
                    <option value="">Select level...</option>
                    {experienceLevelOptions.map(opt => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label style={labelStyle}>Job Type *</label>
                  <select
                    value={formData.job_type}
                    onChange={(e) => setFormData({ ...formData, job_type: e.target.value })}
                    style={inputStyle}
                  >
                    <option value="">Select type...</option>
                    {jobTypeOptions.map(opt => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Row 4: Compensation, Sponsorship/Legal */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-lg)', marginBottom: 'var(--space-lg)' }}>
                <div>
                  <label style={labelStyle}>Compensation</label>
                  <input
                    type="text"
                    value={formData.compensation}
                    onChange={(e) => setFormData({ ...formData, compensation: e.target.value })}
                    style={inputStyle}
                    placeholder="e.g., $100,000 - $150,000/year"
                  />
                </div>
                <div>
                  <label style={labelStyle}>Sponsorship & Legal</label>
                  <select
                    value={formData.sponsorship_and_legal}
                    onChange={(e) => setFormData({ ...formData, sponsorship_and_legal: e.target.value })}
                    style={inputStyle}
                  >
                    {sponsorshipOptions.map(opt => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Row 4: Skills */}
              <div style={{ marginBottom: 'var(--space-lg)' }}>
                <label style={labelStyle}>Skills * (press Enter or comma to add)</label>
                <div style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: '8px',
                  padding: 'var(--space-md)',
                  background: 'var(--bg-surface)',
                  border: '1px solid var(--border-light)',
                  borderRadius: 'var(--radius-md)',
                  minHeight: '50px'
                }}>
                  {formData.skills.map((skill, idx) => (
                    <span key={idx} style={skillTagStyle}>
                      {skill}
                      <button
                        type="button"
                        onClick={() => removeSkill(skill)}
                        style={removeSkillBtnStyle}
                        onMouseOver={(e) => e.currentTarget.style.background = 'rgba(99, 102, 241, 0.3)'}
                        onMouseOut={(e) => e.currentTarget.style.background = 'none'}
                      >
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <line x1="18" y1="6" x2="6" y2="18"/>
                          <line x1="6" y1="6" x2="18" y2="18"/>
                        </svg>
                      </button>
                    </span>
                  ))}
                  <input
                    type="text"
                    value={skillInput}
                    onChange={(e) => setSkillInput(e.target.value)}
                    onKeyDown={handleSkillInputKeyDown}
                    onBlur={() => skillInput.trim() && addSkill(skillInput)}
                    style={{
                      flex: 1,
                      minWidth: '120px',
                      background: 'transparent',
                      border: 'none',
                      outline: 'none',
                      color: 'var(--text-primary)',
                      fontSize: '1rem',
                      fontFamily: 'var(--font-body)'
                    }}
                    placeholder={formData.skills.length === 0 ? 'Type skills...' : ''}
                  />
                </div>
              </div>

              {/* Divider */}
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: 'var(--space-md)',
                margin: 'var(--space-xl) 0',
                color: 'var(--text-muted)'
              }}>
                <div style={{ flex: 1, height: '1px', background: 'var(--border-subtle)' }} />
                <span style={{ fontSize: '0.875rem', fontWeight: '600' }}>Job Description</span>
                <div style={{ flex: 1, height: '1px', background: 'var(--border-subtle)' }} />
              </div>

              {/* Company/Team Overview */}
              <div style={{ marginBottom: 'var(--space-lg)' }}>
                <label style={labelStyle}>Company/Team Overview</label>
                <textarea
                  value={formData.job_description_structured.companyOverview}
                  onChange={(e) => updateStructuredField('companyOverview', e.target.value)}
                  style={{
                    ...inputStyle,
                    minHeight: '80px',
                    resize: 'vertical',
                    lineHeight: '1.6'
                  }}
                  placeholder="About the company or team..."
                />
              </div>

              {/* Role Summary */}
              <div style={{ marginBottom: 'var(--space-lg)' }}>
                <label style={labelStyle}>Role Summary</label>
                <textarea
                  value={formData.job_description_structured.roleSummary}
                  onChange={(e) => updateStructuredField('roleSummary', e.target.value)}
                  style={{
                    ...inputStyle,
                    minHeight: '80px',
                    resize: 'vertical',
                    lineHeight: '1.6'
                  }}
                  placeholder="Brief overview of the role..."
                />
              </div>

              {/* Responsibilities */}
              <div style={{ marginBottom: 'var(--space-lg)' }}>
                <label style={labelStyle}>Responsibilities (one per line)</label>
                <textarea
                  value={arrayToText(formData.job_description_structured.responsibilities)}
                  onChange={(e) => updateStructuredField('responsibilities', textToArray(e.target.value))}
                  style={{
                    ...inputStyle,
                    minHeight: '120px',
                    resize: 'vertical',
                    lineHeight: '1.6'
                  }}
                  placeholder="Design and implement new features&#10;Collaborate with cross-functional teams&#10;Write clean, maintainable code"
                />
              </div>

              {/* Required Qualifications */}
              <div style={{ marginBottom: 'var(--space-lg)' }}>
                <label style={labelStyle}>Required Qualifications (one per line)</label>
                <textarea
                  value={arrayToText(formData.job_description_structured.requiredQualifications)}
                  onChange={(e) => updateStructuredField('requiredQualifications', textToArray(e.target.value))}
                  style={{
                    ...inputStyle,
                    minHeight: '120px',
                    resize: 'vertical',
                    lineHeight: '1.6'
                  }}
                  placeholder="3+ years of experience in software development&#10;Proficiency in React and TypeScript&#10;Strong problem-solving skills"
                />
              </div>

              {/* Preferred Qualifications */}
              <div style={{ marginBottom: 'var(--space-lg)' }}>
                <label style={labelStyle}>Preferred Qualifications (one per line)</label>
                <textarea
                  value={arrayToText(formData.job_description_structured.preferredQualifications)}
                  onChange={(e) => updateStructuredField('preferredQualifications', textToArray(e.target.value))}
                  style={{
                    ...inputStyle,
                    minHeight: '100px',
                    resize: 'vertical',
                    lineHeight: '1.6'
                  }}
                  placeholder="Experience with cloud platforms (AWS, GCP)&#10;Familiarity with CI/CD pipelines"
                />
              </div>

              {/* Benefits */}
              <div style={{ marginBottom: 'var(--space-lg)' }}>
                <label style={labelStyle}>Benefits & Compensation Details</label>
                <textarea
                  value={formData.job_description_structured.benefits}
                  onChange={(e) => updateStructuredField('benefits', e.target.value)}
                  style={{
                    ...inputStyle,
                    minHeight: '80px',
                    resize: 'vertical',
                    lineHeight: '1.6'
                  }}
                  placeholder="Health insurance, 401k, unlimited PTO..."
                />
              </div>

              {/* Additional Requirements */}
              <div style={{ marginBottom: 'var(--space-xl)' }}>
                <label style={labelStyle}>Additional Requirements</label>
                <textarea
                  value={formData.job_description_structured.additionalRequirements}
                  onChange={(e) => updateStructuredField('additionalRequirements', e.target.value)}
                  style={{
                    ...inputStyle,
                    minHeight: '80px',
                    resize: 'vertical',
                    lineHeight: '1.6'
                  }}
                  placeholder="Work schedule, travel requirements, etc..."
                />
              </div>

              {error && (
                <div style={{
                  padding: 'var(--space-md)',
                  background: 'rgba(239, 68, 68, 0.15)',
                  border: '1px solid rgba(239, 68, 68, 0.3)',
                  borderRadius: 'var(--radius-md)',
                  color: 'var(--red-500)',
                  marginBottom: 'var(--space-lg)'
                }}>
                  {error}
                </div>
              )}

              <div style={{ display: 'flex', gap: 'var(--space-md)', justifyContent: 'flex-end' }}>
                <button
                  type="button"
                  onClick={handleCloseForm}
                  disabled={submitting}
                  style={{
                    padding: 'var(--space-md) var(--space-xl)',
                    background: 'var(--bg-surface)',
                    color: 'var(--text-primary)',
                    border: '1px solid var(--border-light)',
                    borderRadius: 'var(--radius-lg)',
                    fontSize: '1rem',
                    fontWeight: '600',
                    fontFamily: 'var(--font-body)',
                    cursor: submitting ? 'not-allowed' : 'pointer'
                  }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  style={{
                    padding: 'var(--space-md) var(--space-xl)',
                    background: submitting ? 'var(--bg-surface)' : 'var(--gradient-primary)',
                    color: submitting ? 'var(--text-tertiary)' : 'white',
                    border: submitting ? '1px solid var(--border-light)' : 'none',
                    borderRadius: 'var(--radius-lg)',
                    fontSize: '1rem',
                    fontWeight: '700',
                    fontFamily: 'var(--font-body)',
                    cursor: submitting ? 'not-allowed' : 'pointer',
                    boxShadow: submitting ? 'none' : 'var(--glow-indigo)'
                  }}
                >
                  {submitting ? 'Saving...' : (editingJob ? 'Update Job' : 'Create Job')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
};

export default AdminJobs;
